import React, { useState, useEffect } from "react";

interface IgnoredUser {
  id: string;
  username: string;
  reason?: string;
  createdAt: string;
}

interface IgnoredUsersManagerProps {
  token: string;
}

export const IgnoredUsersManager: React.FC<IgnoredUsersManagerProps> = ({
  token,
}) => {
  const [ignoredUsers, setIgnoredUsers] = useState<IgnoredUser[]>([]);
  const [newUsername, setNewUsername] = useState("");
  const [newReason, setNewReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchIgnoredUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIgnoredUsers = async () => {
    try {
      const response = await fetch("/api/bot/ignored-users", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIgnoredUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch ignored users:", error);
    }
  };

  const handleAdd = async () => {
    if (!newUsername.trim()) {
      setError("Username is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/bot/ignored-users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: newUsername.trim().toLowerCase(),
          reason: newReason.trim() || undefined,
        }),
      });

      if (response.ok) {
        setNewUsername("");
        setNewReason("");
        await fetchIgnoredUsers();
      } else {
        const data = await response.json();
        setError(data.message || "Failed to add user to ignore list");
      }
    } catch (error) {
      setError("Failed to add user to ignore list");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (username: string) => {
    if (!window.confirm(`Remove ${username} from the ignore list?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/bot/ignored-users/${username}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchIgnoredUsers();
      }
    } catch (error) {
      console.error("Failed to remove user from ignore list:", error);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#2d3748",
        borderRadius: "8px",
        padding: "20px",
        marginTop: "20px",
        border: "1px solid #4a5568",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <h3 style={{ color: "#e2e8f0", marginTop: 0 }}>Ignored Users</h3>

      <p style={{ color: "#cbd5e0", fontSize: "14px", marginBottom: "20px" }}>
        Messages from ignored users will not be processed, stored, or trigger
        any bot responses. This is useful for ignoring other bots or spam
        accounts. Usernames are case-insensitive (e.g., &quot;UserName&quot; and
        &quot;username&quot; are treated as the same).
      </p>

      {/* Add new ignored user form */}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "10px",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            placeholder="Username to ignore"
            style={{
              flex: "1 1 200px",
              minWidth: "150px",
              padding: "10px",
              backgroundColor: "#1a202c",
              color: "#e2e8f0",
              border: "1px solid #4a5568",
              borderRadius: "4px",
              fontSize: "14px",
              outline: "none",
            }}
          />
          <input
            type="text"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            placeholder="Reason (optional)"
            style={{
              flex: "1 1 200px",
              minWidth: "150px",
              padding: "10px",
              backgroundColor: "#1a202c",
              color: "#e2e8f0",
              border: "1px solid #4a5568",
              borderRadius: "4px",
              fontSize: "14px",
              outline: "none",
            }}
          />
          <button
            onClick={handleAdd}
            disabled={loading || !newUsername.trim()}
            style={{
              backgroundColor: "#9146FF",
              color: "white",
              border: "none",
              padding: "10px 20px",
              fontSize: "14px",
              borderRadius: "4px",
              cursor:
                loading || !newUsername.trim() ? "not-allowed" : "pointer",
              opacity: loading || !newUsername.trim() ? 0.5 : 1,
              minWidth: "80px",
            }}
          >
            {loading ? "Adding..." : "Add"}
          </button>
        </div>
        {error && (
          <div style={{ color: "#fc8181", fontSize: "14px", marginTop: "5px" }}>
            {error}
          </div>
        )}
      </div>

      {/* List of ignored users */}
      <div>
        {ignoredUsers.length === 0 ? (
          <div
            style={{
              backgroundColor: "#1a202c",
              padding: "15px",
              borderRadius: "4px",
              fontSize: "14px",
              color: "#cbd5e0",
              textAlign: "center",
            }}
          >
            No ignored users
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            {ignoredUsers.map((user) => (
              <div
                key={user.id}
                style={{
                  backgroundColor: "#1a202c",
                  padding: "15px",
                  borderRadius: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid #4a5568",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#e2e8f0", fontSize: "16px" }}>
                    @{user.username}
                  </div>
                  {user.reason && (
                    <div
                      style={{
                        color: "#718096",
                        fontSize: "14px",
                        marginTop: "5px",
                      }}
                    >
                      {user.reason}
                    </div>
                  )}
                  <div
                    style={{
                      color: "#718096",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    Added {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(user.username)}
                  style={{
                    backgroundColor: "#e53e3e",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#c53030")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e53e3e")
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
